import {SecureStoragePlugin} from 'capacitor-secure-storage-plugin';
import {Preferences} from '@capacitor/preferences';
import {type StorageLikeAsync} from '@vueuse/core';

const requestsInProgress = new Set<Promise<unknown>>();

async function trackRequest<T>(promise: Promise<T>): Promise<T> {
    requestsInProgress.add(promise);
    try {
        return await promise;
    } finally {
        requestsInProgress.delete(promise);
    }
}

export const capacitorStorage: StorageLikeAsync = {
    async getItem(key) {
        return trackRequest(
            SecureStoragePlugin.get({key})
                .then(({value}) => value)
                .catch(() => null)
        );
    },
    async setItem(key, value) {
        return SecureStoragePlugin.set({key, value}).then(() => {});
    },
    async removeItem(key) {
        return SecureStoragePlugin.remove({key})
            .then(() => {})
            .catch(() => {});
    },
};

export async function allRequestsSettled() {
    if (requestsInProgress.size === 0) {
        return;
    }
    await Promise.allSettled(requestsInProgress);
}

// Non-sensitive flags stored in Capacitor Preferences (UserDefaults/SharedPreferences)
// rather than the Keychain, avoiding slow XPC calls to securityd on iOS.
// getItem reads are tracked so allRequestsSettled() waits for them — they queue
// behind Keychain reads on the serial bridge and would otherwise not be settled
// when handleTokenRefresh() runs.
export const preferencesStorage: StorageLikeAsync = {
    async getItem(key) {
        return trackRequest(Preferences.get({key}).then(({value}) => value));
    },
    async setItem(key, value) {
        return Preferences.set({key, value});
    },
    async removeItem(key) {
        return Preferences.remove({key}).catch(() => {});
    },
};
